import { useState } from 'react';
import { ArrowClockwiseIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { NotificationCard } from '@/components/notifications/NotificationCard';
import { useNotifications, useNotificationCount } from '@/lib/hooks/useNotifications';
import { useI18n } from '@/i18n/I18nProvider';
import Spinner from '@/components/ui/spinner';

export default function NotificationsPage() {
  const { locale, t } = useI18n();
  const isRTL = locale === 'ar';

  const [statusFilter, setStatusFilter] = useState<'all' | 'read' | 'unread'>('all');
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Fetch notifications with filters
  const { notifications, total, isLoading, markAsRead, markAllAsRead, refresh } = useNotifications({
    status: statusFilter,
    limit,
    offset,
  });

  // Fetch count for header
  const { unread, refresh: refreshCount } = useNotificationCount();

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    await markAllAsRead();
    refreshCount();
  };

  // Handle refresh
  const handleRefresh = () => {
    refresh();
    refreshCount();
  };

  // Handle load more
  const handleLoadMore = () => {
    setOffset(offset + limit);
  };

  // Reset offset when filters change
  const handleStatusChange = (value: string) => {
    setStatusFilter(value as 'all' | 'read' | 'unread');
    setOffset(0);
  };

  const hasMore = notifications.length + offset < total;

  return (
    <div className={isRTL ? 'rtl' : 'ltr'}>
      <div className="py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t.notifications.title}</h1>
              {unread > 0 && (
                <p className="mt-1 text-sm text-muted-foreground">
                  {unread} {t.notifications.unreadStatus}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
                <ArrowClockwiseIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span className={isRTL ? 'mr-2' : 'ml-2'}>{t.notifications.refresh}</span>
              </Button>

              {unread > 0 && (
                <Button variant="default" size="sm" onClick={handleMarkAllRead}>
                  {t.notifications.markAllAsRead}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Status Tabs */}
            <Tabs value={statusFilter} onValueChange={handleStatusChange}>
              <TabsList>
                <TabsTrigger value="all">{t.notifications.all}</TabsTrigger>
                <TabsTrigger value="unread">{t.notifications.unread}</TabsTrigger>
                <TabsTrigger value="read">{t.notifications.read}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Count */}
          {total > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {t.notifications.showing} {Math.min(notifications.length + offset, total)} {t.notifications.of} {total}
            </p>
          )}
        </Card>

        {/* Notifications List */}
        {isLoading && notifications.length === 0 ? (
          <Spinner />
        ) : notifications.length === 0 ? (
          <Card className="flex h-64 items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium text-muted-foreground">{t.notifications.noNotifications}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t.notifications.noNotificationsDesc}</p>
            </div>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {notifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onRead={markAsRead}
                  showFullBody={true}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="mt-6 flex justify-center">
                <Button variant="outline" onClick={handleLoadMore} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <ArrowClockwiseIcon className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t.common.loading}
                    </>
                  ) : (
                    t.notifications.loadMore
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
